import authService, { TOKEN_TYPES } from "./authService";
import { buildApiRequestUrl, isAbsoluteUrl } from "@/utils/apiUrl";

const BASE_API_URL = import.meta.env.VITE_API_URL;

const resolveDownloadUrl = (
  downloadURL: string,
  trailingSlash = false,
): string => {
  if (isAbsoluteUrl(downloadURL)) {
    return downloadURL;
  }

  const normalizedDownloadUrl = trailingSlash ? `${downloadURL}/` : downloadURL;

  if (isAbsoluteUrl(normalizedDownloadUrl)) {
    return normalizedDownloadUrl;
  }

  return buildApiRequestUrl({
    baseUrl: BASE_API_URL,
    endpointSuffix: normalizedDownloadUrl,
  });
};

export const downloadService = {
  downloadFileFromUrl: async (
    fileURL: string,
    fileName: string,
  ): Promise<void> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(fileURL, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || "download");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  downloadApiFileWithGET: async (
    downloadURL: string,
    fileName: string,
  ): Promise<void> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(resolveDownloadUrl(downloadURL), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get("content-type");
    if (
      !contentType ||
      (!contentType.includes("application/zip") &&
        !contentType.includes("application/octet-stream"))
    ) {
      console.warn("Unexpected content type:", contentType);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName || "download"}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  downloadApiFileWithPOST: async (
    downloadURL: string,
    fileName: string,
    payload: unknown,
  ): Promise<void> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(resolveDownloadUrl(downloadURL, true), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get("content-type");
    if (
      !contentType ||
      (!contentType.includes("application/zip") &&
        !contentType.includes("application/octet-stream"))
    ) {
      console.warn("Unexpected content type:", contentType);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName || "download"}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  createBlobUrlFromFile: async (fileURL: string): Promise<string> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(fileURL, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};
